<template>
    <div
        ref="root"
        class="modern-select"
        :class="{ 'is-open': isOpen, 'is-disabled': disabled }"
        @keydown.esc.stop="close"
    >
        <button
            :id="id"
            class="modern-select__trigger"
            type="button"
            :disabled="disabled"
            :aria-expanded="String(isOpen)"
            aria-haspopup="listbox"
            :aria-controls="listboxId"
            @click="toggle"
            @keydown.down.prevent="open"
            @keydown.up.prevent="open"
        >
            <span :class="{ 'is-placeholder': !selectedOption }">
                {{ selectedOption?.text || placeholder || localizedPlaceholder }}
            </span>
            <svg aria-hidden="true" viewBox="0 0 20 20">
                <path d="m5.5 7.5 4.5 4.5 4.5-4.5"/>
            </svg>
        </button>

        <div v-if="isOpen" class="modern-select__popover">
            <label v-if="isSearchable" class="modern-select__search">
                <svg aria-hidden="true" viewBox="0 0 20 20">
                    <circle cx="8.5" cy="8.5" r="5.5"/>
                    <path d="m13 13 4 4"/>
                </svg>
                <span class="sr-only">{{ localizedSearch }}</span>
                <input
                    ref="searchInput"
                    v-model="query"
                    type="search"
                    :placeholder="localizedSearch"
                    autocomplete="off"
                    @keydown.down.prevent="focusFirstOption"
                >
            </label>

            <div
                :id="listboxId"
                class="modern-select__list"
                role="listbox"
                :aria-labelledby="id || undefined"
            >
                <button
                    v-for="option in filteredOptions"
                    :key="String(option.id)"
                    class="modern-select__option"
                    :class="{ 'is-selected': isSelected(option) }"
                    type="button"
                    role="option"
                    :aria-selected="String(isSelected(option))"
                    @click="selectOption(option)"
                >
                    <span>{{ option.text }}</span>
                    <svg v-if="isSelected(option)" aria-hidden="true" viewBox="0 0 20 20">
                        <path d="m4 10 4 4 8-9"/>
                    </svg>
                </button>

                <p v-if="filteredOptions.length === 0" class="modern-select__empty">
                    {{ localizedEmpty }}
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from 'vue';

const props = defineProps({
    id: {
        type: String,
        default: '',
    },
    modelValue: {
        type: [String, Number],
        default: '',
    },
    options: {
        type: Array,
        default: () => [],
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    placeholder: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['update:modelValue', 'change']);
const root = ref(null);
const searchInput = ref(null);
const isOpen = ref(false);
const query = ref('');

const currentLocale = document.documentElement.lang?.toLowerCase() || 'am';
const copy = {
    am: {
        placeholder: 'Ընտրել',
        search: 'Փնտրել',
        empty: 'Տարբերակ չի գտնվել',
    },
    ru: {
        placeholder: 'Выбрать',
        search: 'Поиск',
        empty: 'Ничего не найдено',
    },
    en: {
        placeholder: 'Select',
        search: 'Search',
        empty: 'No options found',
    },
};
const localeCopy = copy[currentLocale] || copy.en;
const localizedPlaceholder = localeCopy.placeholder;
const localizedSearch = localeCopy.search;
const localizedEmpty = localeCopy.empty;
const listboxId = computed(() => `${props.id || 'modern-select'}-listbox`);

const normalizedOptions = computed(() => props.options.map((option) => {
    if (option !== null && typeof option === 'object') {
        return {
            id: option.id,
            text: String(option.text ?? option.name ?? option.id),
        };
    }

    return {
        id: option,
        text: String(option),
    };
}));

const selectedOption = computed(() => normalizedOptions.value.find(
    (option) => String(option.id) === String(props.modelValue),
));

const isSearchable = computed(() => normalizedOptions.value.length > 8);
const filteredOptions = computed(() => {
    const term = query.value.trim().toLocaleLowerCase(currentLocale);

    if (!term) {
        return normalizedOptions.value;
    }

    return normalizedOptions.value.filter((option) => (
        option.text.toLocaleLowerCase(currentLocale).includes(term)
    ));
});

const isSelected = (option) => String(option.id) === String(props.modelValue);

const open = async () => {
    if (props.disabled || isOpen.value) {
        return;
    }

    isOpen.value = true;
    query.value = '';

    if (isSearchable.value) {
        await nextTick();
        searchInput.value?.focus();
    }
};

const close = () => {
    isOpen.value = false;
    query.value = '';
};

const toggle = () => {
    if (isOpen.value) {
        close();
        return;
    }

    open();
};

const selectOption = (option) => {
    if (!isSelected(option)) {
        emit('update:modelValue', option.id);
        emit('change', option.id);
    }

    close();
};

const focusFirstOption = async () => {
    await nextTick();
    root.value?.querySelector('.modern-select__option')?.focus();
};

const handleOutsideClick = (event) => {
    if (isOpen.value && !root.value?.contains(event.target)) {
        close();
    }
};

watch(() => props.disabled, (isDisabled) => {
    if (isDisabled) {
        close();
    }
});

onMounted(() => {
    document.addEventListener('pointerdown', handleOutsideClick);
});

onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', handleOutsideClick);
});
</script>

<style scoped>
.modern-select {
    position: relative;
    width: 100%;
    height: auto !important;
    min-height: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: transparent !important;
    border: 0 !important;
    border-radius: 0 !important;
}

.modern-select__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 52px;
    padding: 0 16px;
    color: #171a20;
    background: #fafafa;
    border: 1px solid #dfe3e9;
    border-radius: 14px;
    cursor: pointer;
    gap: 16px;
    text-align: left;
    transition: border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.modern-select__trigger:hover {
    background: #fff;
    border-color: #c8ced7;
}

.modern-select__trigger:focus-visible,
.is-open .modern-select__trigger {
    outline: none;
    background: #fff;
    border-color: #e31e2f;
    box-shadow: 0 0 0 4px rgba(227, 30, 47, .1);
}

.modern-select__trigger span {
    min-width: 0;
    overflow: hidden;
    font-size: 14px;
    font-weight: 550;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.modern-select__trigger span.is-placeholder {
    color: #8a919e;
}

.modern-select__trigger > svg {
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    fill: none;
    stroke: #667085;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.8;
    transition: transform 180ms ease;
}

.is-open .modern-select__trigger > svg {
    transform: rotate(180deg);
}

.modern-select__popover {
    position: absolute;
    z-index: 80;
    top: calc(100% + 8px);
    right: 0;
    left: 0;
    padding: 8px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #e1e4e9;
    border-radius: 16px;
    box-shadow: 0 22px 60px rgba(16, 24, 40, .2);
}

.modern-select__search {
    position: relative;
    display: block;
    margin-bottom: 7px;
}

.modern-select__search > svg {
    position: absolute;
    top: 50%;
    left: 13px;
    width: 17px;
    height: 17px;
    fill: none;
    stroke: #7a8290;
    stroke-linecap: round;
    stroke-width: 1.6;
    transform: translateY(-50%);
}

.modern-select__search input {
    width: 100%;
    height: 42px;
    padding: 0 12px 0 40px;
    color: #171a20;
    background: #f6f7f8;
    border: 1px solid transparent;
    border-radius: 11px;
    outline: none;
    font-size: 13px;
}

.modern-select__search input:focus {
    background: #fff;
    border-color: rgba(227, 30, 47, .45);
}

.modern-select__list {
    display: grid;
    max-height: 290px;
    padding: 2px;
    overflow-y: auto;
    overscroll-behavior: contain;
    gap: 2px;
}

.modern-select__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 40px;
    padding: 8px 11px;
    color: #343a45;
    background: transparent;
    border: 0;
    border-radius: 10px;
    cursor: pointer;
    gap: 15px;
    text-align: left;
}

.modern-select__option:hover,
.modern-select__option:focus-visible {
    outline: none;
    color: #171a20;
    background: #f4f5f7;
}

.modern-select__option.is-selected {
    color: #b90f1e;
    background: #fff1f2;
    font-weight: 700;
}

.modern-select__option span {
    font-size: 13px;
    line-height: 1.35;
}

.modern-select__option svg {
    flex: 0 0 auto;
    width: 17px;
    height: 17px;
    fill: none;
    stroke: #e31e2f;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2;
}

.modern-select__empty {
    margin: 0;
    padding: 18px 12px;
    color: #8a919e;
    font-size: 13px;
    text-align: center;
}

.is-disabled {
    opacity: .6;
}

.is-disabled .modern-select__trigger {
    cursor: not-allowed;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
    white-space: nowrap;
}
</style>
